import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CustomPagination } from 'components/featured/Deals/components/DealsTable/Pagination';
import ClientAvatar from 'components/shared/ClientAvatar';
import SectionLoader from 'components/shared/SectionLoader';
import { SortPopover } from 'components/shared/SortPopOver';
import { formatDealAmount } from 'helper/short-currency';
import { DealsTableColumns } from 'model/deals.model';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { BusinessesService } from 'service/businesses.service';
import zeplinColor, { dealStageColors } from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { columns, outcomeColorMap, sortLabels } from './data';
import { muiStyles } from './muiStyles';
import { Deal } from './types';

const Relationship = () => {
  return (
    <div>
      <Typography variant='responsiveHeading3'>Complete Deal Timeline</Typography>
      <RelationshipTable />
    </div>
  );
};

export default Relationship;

export const RelationshipTable = () => {
  const { id } = useParams();
  const [sortAnchor, setSortAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; order: 'asc' | 'desc' }>({
    field: 'dealName',
    order: 'asc',
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(10);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const fetchDeals = async (id: string) => {
      try {
        setLoading(true);
        const res = await BusinessesService.getBusinessDeals(id, {
          page: currentPage,
          limit: rowsPerPage,
        });
        setData(res.deals);
        setTotalCount(res.total || 0);
        setTotalPages(res.totalPages || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDeals(id);
    }
  }, [id, currentPage, rowsPerPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage < totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRowsChange = (newRows: number) => {
    setRowsPerPage(newRows);
    setCurrentPage(1);
  };

  if (loading) return <SectionLoader />;

  return (
    <Box sx={muiStyles.section}>
      <Paper sx={muiStyles.paper}>
        <TableContainer sx={muiStyles.tableContainer}>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow sx={muiStyles.headerRowStyles}>
                {columns.map((column) => {
                  // const isSortable = sortableColumns.includes(column.id);
                  // NOTE: Hardcoded sorting until backend will integrate sorting for this table
                  const isSortable = false;

                  return (
                    <TableCell
                      key={column.id}
                      onClick={(e) =>
                        isSortable && setSortAnchor({ el: e.currentTarget, id: column.id })
                      }
                    >
                      <Box sx={muiStyles.columnTitle}>
                        <Typography variant='body3'>{column.label}</Typography>
                        {isSortable && (
                          <Box sx={muiStyles.sortBox} aria-label='button'>
                            <UnfoldMoreIcon sx={muiStyles.unfoldIcon} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>Loading deals...</Typography>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={DealsTableColumns.length} align='center'>
                    <Typography variant='body2'>No deals found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row: any, ind) => {
                  const getRowColor = (): { borderColor: string; bgColor: string } => {
                    return { borderColor: zeplinColor.Neutral5, bgColor: 'none' };
                  };

                  const { borderColor, bgColor } = getRowColor();

                  return (
                    <TableRow
                      hover
                      key={ind}
                      component={Link}
                      to={`${RoutePaths.Deals}/${row.dealId}`}
                      sx={{
                        ...muiStyles.row,
                        bgcolor: bgColor === 'none' ? 'transparent' : bgColor,
                        borderLeft: `2px solid ${borderColor}`,
                        '&:last-child td, &:last-child th': {
                          borderBottom: `2px solid ${borderColor}`,
                        },
                      }}
                    >
                      {columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              ...muiStyles.cell,
                              borderBottom: `1px solid ${borderColor}`,
                              borderTop: `1px solid ${borderColor}`,
                            }}
                          >
                            {renderCell(column.id, row)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <SortPopover
        anchorEl={sortAnchor?.el || null}
        setAnchorEl={() => setSortAnchor(null)}
        sortOption={sortConfig.field === sortAnchor?.id ? sortConfig.order : 'asc'}
        onSortChange={(order) => {
          if (sortAnchor) {
            setSortConfig({ field: sortAnchor.id, order });
            setCurrentPage(1);
          }
          setSortAnchor(null);
        }}
        label={DealsTableColumns.find((c) => c.id === sortAnchor?.id)?.label || ''}
        ascLabel={sortAnchor ? sortLabels[sortAnchor.id]?.asc : undefined}
        descLabel={sortAnchor ? sortLabels[sortAnchor.id]?.desc : undefined}
      />

      <CustomPagination
        currentPage={currentPage - 1}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
      />
    </Box>
  );
};

const renderCell = (columnId: string, row: Deal) => {
  const renders: Record<string, () => JSX.Element | string> = {
    clientName: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <ClientAvatar
          profilePicture={row.clientProfilePicture}
          clientName={row.clientName}
          sx={{ width: '28px', height: '28px' }}
        />
        <Typography variant='responsiveBody2'>{row.clientName}</Typography>
      </Box>
    ),
    name: () => (
      <Typography variant='responsiveBody3' noWrap sx={{ textOverflow: 'ellipsis' }}>
        {row.name}
      </Typography>
    ),
    amount: () => {
      return `${formatDealAmount(row.amount)}`;
    },
    stageName: () => {
      const stageName = row.stageName || '';
      return (
        <Chip
          label={
            <Typography variant='responsiveLabel1' sx={{ color: zeplinColor.Neutral1 }}>
              {stageName ? Utilities.formatSnakeToTitle(stageName) : 'N/A'}
            </Typography>
          }
          sx={{
            bgcolor: dealStageColors[row.stageName] || dealStageColors.null,
            height: '22px',
          }}
        />
      );
    },
    outcome: () => (
      <Chip
        label={
          <Typography variant='responsiveLabel1' sx={{ color: zeplinColor.Neutral1 }}>
            {row.outcome ? Utilities.toCamelCase(row.outcome) : 'Null'}
          </Typography>
        }
        sx={{
          bgcolor: row.outcome ? outcomeColorMap[row.outcome] : zeplinColor.USER_PROFILE_COLOR9,
          height: 22,
        }}
      />
    ),
    closeDate: () => (
      <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '4px' }}>
        <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
          {Utilities.formatDate(row.endDate)}
        </Typography>
      </Box>
    ),
    ownerName: () => <Typography variant='responsiveBody3'>{row.ownerName ?? 'N/A'}</Typography>,
  };

  return renders[columnId] ? renders[columnId]() : (row as any)[columnId];
};
